import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  DollarSign, 
  CheckCircle2, 
  Clock, 
  ExternalLink,
  Mail,
  Loader2,
  AlertCircle,
  CreditCard,
  TrendingUp,
  Calendar,
  Download,
  FileText,
  Shield,
  Eye,
  Copy,
  Link,
  Code,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Card component for consistency
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.06)] ${className}`}>
    {children}
  </div>
);

// Stat card component
const StatCard = ({ icon: Icon, iconColor, label, value, testId }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${iconColor}`} />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-xl font-semibold text-gray-900">{value}</p>
  </Card>
);

const PartnerPortalPage = () => {
  const { partnerId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const connected = searchParams.get('connected');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partner, setPartner] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, count: 0 });
  const [links, setLinks] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [pageLoading, setPageLoading] = useState(false);
  
  // Magic link login state
  const [email, setEmail] = useState('');
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  
  useEffect(() => {
    if (partnerId && token) {
      fetchPartnerData(1);
      if (connected === 'true') {
        toast.success('Stripe account connected successfully!');
        window.history.replaceState({}, '', `/partner-portal/${partnerId}?token=${token}`);
      }
    } else {
      setLoading(false);
    }
  }, [partnerId, token, connected]);
  
  const fetchPartnerData = async (page = 1) => {
    try {
      if (page > 1) setPageLoading(true);
      const response = await fetch(`${API_URL}/api/v2/public/partners/${partnerId}/portal?token=${token}&page=${page}&limit=20`);
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid or expired link. Please request a new one.');
        }
        throw new Error('Failed to load partner data');
      }
      
      const data = await response.json();
      setPartner(data.partner);
      setPayouts(data.payouts || []);
      setStats(data.stats || { total: 0, pending: 0, count: 0 });
      setLinks(data.links || null);
      if (data.pagination) setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };
  
  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    setSendingMagicLink(true);
    
    try {
      const response = await fetch(`${API_URL}/api/v2/public/partners/request-portal-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send login link');
      }
      
      setMagicLinkSent(true);
      toast.success('Login link sent! Check your email.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingMagicLink(false);
    }
  };
  
  const handleConnectStripe = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/public/partners/${partnerId}/connect-stripe?token=${token}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to create Stripe link');
      
      const data = await response.json();
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      }
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleCheckStripeStatus = async () => {
    setCheckingStatus(true);
    try {
      // Call the dedicated Stripe status endpoint
      const response = await fetch(`${API_URL}/api/v2/public/partners/${partnerId}/stripe-dashboard-link?token=${token}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to check status');
      }
      
      const data = await response.json();
      
      if (data.is_fully_verified) {
        toast.success('Stripe account is fully verified!');
        // Refresh partner data to update UI
        await fetchPartnerData();
      } else if (data.status?.needs_more_info && data.dashboard_url) {
        toast.info(data.status.message);
        // Open the Stripe onboarding link to continue setup
        setTimeout(() => {
          window.location.href = data.dashboard_url;
        }, 1500);
      } else {
        toast.info(data.status?.message || 'Stripe is still reviewing your account.');
        // Refresh partner data to update UI
        await fetchPartnerData();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to check status');
    } finally {
      setCheckingStatus(false);
    }
  };
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };
  
  // Export functions
  const exportToCSV = () => {
    if (payouts.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Time', 'Booking Code', 'Amount', 'Percentage', 'Status'];
    const rows = payouts.map(p => [
      new Date(p.created_at).toLocaleDateString(),
      new Date(p.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      p.booking_confirmation_code,
      `$${p.amount?.toFixed(2)}`,
      `${p.percentage_applied}%`,
      p.status
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    downloadFile(csvContent, `partner-payouts-${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
    toast.success('CSV exported');
  };
  
  const exportToJSON = () => {
    if (payouts.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const data = {
      partner: {
        name: partner?.name,
        email: partner?.email,
        type: partner?.partner_type,
        percentage: partner?.percentage
      },
      stats,
      payouts: payouts.map(p => ({
        date: p.created_at,
        booking_code: p.booking_confirmation_code,
        amount: p.amount,
        percentage: p.percentage_applied,
        status: p.status
      })),
      exported_at: new Date().toISOString()
    };
    
    downloadFile(JSON.stringify(data, null, 2), `partner-payouts-${new Date().toISOString().split('T')[0]}.json`, 'application/json');
    toast.success('JSON exported');
  };
  
  const exportToPDF = () => {
    if (payouts.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const totalEarned = payouts.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0);
    
    const html = `
      <html><head><title>Partner Payout Report</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #1a1a1a; }
        h1 { font-size: 22px; margin-bottom: 4px; }
        .subtitle { color: #666; font-size: 13px; margin-bottom: 30px; }
        .summary { display: flex; gap: 24px; margin-bottom: 30px; }
        .stat { background: #f9fafb; border-radius: 8px; padding: 16px 20px; }
        .stat-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; }
        .stat-value { font-size: 20px; font-weight: 600; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 10px 12px; background: #f3f4f6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; }
        td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; }
        .amount { text-align: right; font-weight: 600; }
        .status-completed { color: #15803d; }
        .status-failed { color: #dc2626; }
        .status-pending { color: #d97706; }
        .footer { margin-top: 30px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }
      </style></head><body>
      <h1>Partner Payout Report</h1>
      <p class="subtitle">${partner?.name} &bull; ${partner?.email} &bull; ${partner?.percentage}% Revenue Share</p>
      <div class="summary">
        <div class="stat"><div class="stat-label">Total Earned</div><div class="stat-value">$${totalEarned.toFixed(2)}</div></div>
        <div class="stat"><div class="stat-label">Referrals</div><div class="stat-value">${payouts.length}</div></div>
      </div>
      <table>
        <thead><tr><th>Date & Time</th><th>Booking</th><th style="text-align:right">Amount</th><th>Status</th></tr></thead>
        <tbody>
          ${payouts.map(p => `<tr>
            <td>${new Date(p.created_at).toLocaleDateString()} ${new Date(p.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
            <td>#${p.booking_confirmation_code}</td>
            <td class="amount">$${p.amount?.toFixed(2)} (${p.percentage_applied}%)</td>
            <td class="${p.status === 'completed' ? 'status-completed' : p.status === 'transfer_failed' ? 'status-failed' : 'status-pending'}">${p.status === 'completed' ? 'Transferred' : p.status === 'transfer_failed' ? 'Failed' : 'Pending'}</td>
          </tr>`).join('')}
        </tbody>
      </table>
      <div class="footer">Generated ${new Date().toLocaleString()} &bull; Powered by BookingFlows</div>
      </body></html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
    toast.success('PDF ready to save');
  };
  
  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // ========================================
  // LOGIN FORM (No token)
  // ========================================
  if (!partnerId || !token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="partner-login-page">
        <Card className="p-6 sm:p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <img 
              src="/logo-icon.png" 
              alt="BookingFlows" 
              className="w-12 h-12 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">Partner Portal</h1>
            <p className="text-sm text-gray-600 mt-1">Enter your email to access your dashboard</p>
          </div>
          
          {magicLinkSent ? (
            <div className="text-center" data-testid="magic-link-sent">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Check your email</h2>
              <p className="text-sm text-gray-600">
                We sent a login link to <strong className="text-gray-900">{email}</strong>
              </p>
              <button 
                onClick={() => setMagicLinkSent(false)}
                className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="try-different-email-btn"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleRequestMagicLink} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Partner Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-10"
                  data-testid="partner-email-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={sendingMagicLink}
                data-testid="send-login-link-btn"
              >
                {sendingMagicLink ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Send Login Link'
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                We'll email you a secure link to access your portal
              </p>
            </form>
          )}
        </Card>
      </div>
    );
  }
  
  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  
  // ========================================
  // ERROR STATE
  // ========================================
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="partner-error-page">
        <Card className="p-6 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Access Error</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <Button 
            onClick={() => window.location.href = '/partner-portal'}
            className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="request-new-link-btn"
          >
            Request New Link
          </Button>
        </Card>
      </div>
    );
  }
  
  // ========================================
  // MAIN PORTAL
  // ========================================
  return (
    <div className="min-h-screen bg-white" data-testid="partner-portal">
      {/* Header */}
      <header className="bg-white">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-8 h-8 sm:w-10 sm:h-10"
              />
              <div>
                <h1 className="text-base sm:text-lg font-semibold text-gray-900">Partner Portal</h1>
                <p className="text-xs text-gray-500">{partner?.organization_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                <Eye className="w-3.5 h-3.5" />
                View Only
              </div>
              <Badge 
                className={`shadow-none ${partner?.status === 'connected' || partner?.status === 'active' 
                  ? 'bg-green-50 text-green-700 border-0' 
                  : 'bg-gray-100 text-gray-600 border-0'}`
                }
              >
                {partner?.status}
              </Badge>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Partner Info Card */}
        <Card className="p-5" data-testid="partner-info-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Your Partnership</h2>
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Shield className="w-3.5 h-3.5" />
              Secure
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Name</p>
              <p className="text-sm font-medium text-gray-900">{partner?.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Email</p>
              <p className="text-sm font-medium text-gray-900 truncate">{partner?.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Type</p>
              <p className="text-sm font-medium text-gray-900 capitalize">{partner?.partner_type?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Revenue Share</p>
              <p className="text-sm font-bold text-blue-600">{partner?.percentage}%</p>
            </div>
          </div>
        </Card>
        
        {/* Stripe Status */}
        <Card className="p-5" data-testid="stripe-status-card">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Payment Setup</h2>
          
          {partner?.stripe_onboarding_complete ? (
            // FULLY CONNECTED
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Stripe Connected</p>
                  <p className="text-xs text-green-600">Payouts will be sent automatically</p>
                </div>
              </div>
              <a 
                href="https://dashboard.stripe.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-green-700 hover:text-green-800 font-medium flex items-center gap-1"
                data-testid="stripe-dashboard-link"
              >
                Stripe Dashboard
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ) : partner?.stripe_account_id ? (
            // PENDING VERIFICATION
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800">Stripe Verification in Progress</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Stripe is validating your account. This usually takes 1-5 minutes, but may take longer if additional information is needed.
                    </p>
                    <p className="text-xs text-blue-500 mt-2">
                      You may need to provide ID verification or banking details in your Stripe dashboard.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  onClick={handleCheckStripeStatus}
                  variant="outline"
                  className="h-10 bg-white hover:bg-gray-50"
                  disabled={checkingStatus}
                  data-testid="check-status-btn"
                >
                  {checkingStatus ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Check Status
                </Button>
                <a 
                  href="https://dashboard.stripe.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  Open Stripe Dashboard
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ) : (
            // NOT STARTED
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Stripe Not Connected</p>
                  <p className="text-xs text-amber-600">Connect to receive your earnings</p>
                </div>
              </div>
              <Button 
                onClick={handleConnectStripe}
                className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="connect-stripe-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Connect Stripe Account
              </Button>
            </div>
          )}
        </Card>
        
        {/* Your Links & Embed Codes */}
        {links && (
          <Card className="p-5" data-testid="links-card">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Your Referral Link</h2>
            
            <div className="space-y-4">
              {/* Referral Link */}
              {links.referral_url && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Referral Link</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5 min-w-0 overflow-hidden">
                      <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate hidden sm:block">{links.referral_url}</span>
                      <span className="text-sm text-gray-700 truncate sm:hidden">
                        {links.referral_url.length > 45 
                          ? links.referral_url.slice(0, 22) + '...' + links.referral_url.slice(-20) 
                          : links.referral_url}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      className="h-10 px-3 bg-white hover:bg-gray-50 flex-shrink-0"
                      onClick={() => copyToClipboard(links.referral_url, 'Referral link')}
                      data-testid="copy-referral-link-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">Share this link to track your referrals</p>
                </div>
              )}
            </div>
            
            {/* Services associated (for revenue partners) */}
            {partner?.partner_type === 'revenue_partner' && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  {partner.apply_to_all_services 
                    ? 'You earn on ALL services' 
                    : 'You earn on these services:'}
                </p>
                {!partner.apply_to_all_services && partner.assigned_services?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {partner.assigned_services.map((service, i) => (
                      <Badge key={i} className="bg-blue-50 text-blue-700 border-0 text-xs shadow-none">
                        {service}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <StatCard 
            icon={DollarSign}
            iconColor="text-green-600"
            label="Total Earned"
            value={`$${stats.total?.toFixed(2) || '0.00'}`}
            testId="stat-total-earned"
          />
          <StatCard 
            icon={Clock}
            iconColor="text-amber-500"
            label="Awaiting Transfer"
            value={`$${stats.pending?.toFixed(2) || '0.00'}`}
            testId="stat-pending"
          />
          <StatCard 
            icon={TrendingUp}
            iconColor="text-blue-600"
            label="Referrals"
            value={stats.count || 0}
            testId="stat-referrals"
          />
        </div>
        
        {/* Payouts Table */}
        <Card className="overflow-hidden" data-testid="payouts-table">
          <div className="p-5 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Payout History</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Payouts are transferred to your Stripe account after booking confirmation
              </p>
            </div>
            
            {payouts.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-9 text-sm gap-2 bg-white hover:bg-gray-50">
                    <Download className="w-4 h-4" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToPDF} className="text-sm" data-testid="export-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToCSV} className="text-sm" data-testid="export-csv-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToJSON} className="text-sm" data-testid="export-json-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          
          {payouts.length === 0 ? (
            <div className="px-5 pb-8 pt-4 text-center" data-testid="empty-payouts">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900">No payouts yet</p>
              <p className="text-xs text-gray-500 mt-1">Earnings will appear here once bookings are made through your links</p>            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking</th>
                    <th className="px-3 sm:px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-3 sm:px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payouts.map((payout) => (
                    <tr key={payout.id} className="hover:bg-gray-50/50">
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-sm text-gray-600">
                        <div>{new Date(payout.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-400">{new Date(payout.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-xs sm:text-sm font-mono text-gray-900">
                        #{payout.booking_confirmation_code}
                      </td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-sm text-gray-900 text-right">
                        <span className="font-semibold">${payout.amount?.toFixed(2)}</span>
                        <span className="text-xs text-gray-500 ml-1">({payout.percentage_applied}%)</span>
                      </td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-center">
                        <Badge 
                          className={`shadow-none ${
                            payout.status === 'completed' || payout.status === 'paid'
                              ? 'bg-green-50 text-green-700 border-0 text-xs' 
                              : payout.status === 'pending'
                              ? 'bg-amber-50 text-amber-700 border-0 text-xs'
                              : payout.status === 'pending_stripe'
                              ? 'bg-blue-50 text-blue-700 border-0 text-xs'
                              : payout.status === 'transfer_failed'
                              ? 'bg-red-50 text-red-700 border-0 text-xs'
                              : 'bg-gray-100 text-gray-600 border-0 text-xs'
                          }`}
                          title={
                            payout.status === 'pending' 
                              ? 'Awaiting transfer to your Stripe account' 
                              : payout.status === 'completed' 
                              ? 'Transferred to your Stripe account'
                              : payout.status === 'pending_stripe'
                              ? 'Connect your Stripe account to receive this payout'
                              : payout.status === 'transfer_failed'
                              ? 'Transfer failed - will retry'
                              : ''
                          }
                        >
                          {payout.status === 'pending' ? 'Awaiting Transfer' 
                            : payout.status === 'completed' ? 'Transferred'
                            : payout.status === 'pending_stripe' ? 'Connect Stripe'
                            : payout.status === 'transfer_failed' ? 'Failed'
                            : payout.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-3 sm:px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} payouts)
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={pagination.page <= 1 || pageLoading}
                    onClick={() => fetchPartnerData(pagination.page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={pagination.page >= pagination.pages || pageLoading}
                    onClick={() => fetchPartnerData(pagination.page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </Card>
      </main>
      
      {/* Footer */}
      <footer className="max-w-3xl mx-auto px-4 py-8 text-center">
        <p className="text-xs text-gray-400">Powered by BookingFlows</p>
      </footer>
    </div>
  );
};

export default PartnerPortalPage;
